import React, { FC, useEffect, useMemo } from 'react';
import { Alert, message } from 'antd';
import * as apiService from '@/service';
import { useFlow, CallNext, GetNext, useUrlQueryState, useSuperState } from '@@/react-hooks';
import { CommonLabelProps } from '../../interface';
import { OffersDisplay } from './component/offers-display';
import { SimilarSpus } from './component/similar-spus';
import { ItemForVerification, OfferGroupForVerification } from '@/interface/label';
import { PureAntdLoading } from '@/ui/pure-antd-loading';
import { LoadableAntdButton } from '@/ui/loadable-antd-button';
import { useAccountInfo } from '@/store/account';
import { LabelLayout, LabelLayoutHeader } from '../layout';
import { SpuDisplay } from '../spu-display';
import { NoMoreLabeling } from '../no-more-labeling';
import { GetVerifyData } from '@/service/interface';
import { LabelerURLQuery } from '@/interface/url';
import { useAccountLabelInfo } from '@/store/account-label-info';
import { LabelType } from '@/enum';
import { MaybePromise } from '@@/util-types';
import commonStyles from '../../common.module.less';
import moment from 'moment';

/**
 * submission data for correction labeling
 */
interface VerificationSubmission {
  offerGroupsWithError: OfferGroupForVerification[]; // offers with error
  spuIdsToCombine: number[]; // spuIds to combine
}

const VerifyLabelCore: FC<
  CommonLabelProps & {
    itemForVerification: ItemForVerification;
    dispatch(submission: VerificationSubmission): MaybePromise<void>;
  }
> = ({ itemForVerification, dispatch, ...filterProps }) => {
  // 标注数据
  const [labelState, setLabelState] = useSuperState<VerificationSubmission>({
    offerGroupsWithError: [],
    spuIdsToCombine: [],
  });

  // initialize labeling state while spu mutated
  useEffect(() => {
    setLabelState({
      offerGroupsWithError: [],
      spuIdsToCombine: [],
    });
  }, []);

  // 内容节点
  const contentNode = (() => {
    const toggleOfferGroup = (offerGroup: OfferGroupForVerification) =>
      setLabelState(prev => {
        const offerGroupsWithError = prev.offerGroupsWithError.includes(offerGroup)
          ? prev.offerGroupsWithError.filter(item => item !== offerGroup)
          : [...prev.offerGroupsWithError, offerGroup];
        return {
          ...prev,
          offerGroupsWithError,
        };
      });

    const onChangeSelectedSpuIds = (spuIdsToCombine: number[]) =>
      setLabelState(prev => ({
        ...prev,
        spuIdsToCombine,
      }));

    return (
      <>
        <div className={commonStyles.left_of_3}>
          <div className={commonStyles.vertical_container}>
            <header className={commonStyles.header}>
              <h3 className={commonStyles.title}>当前商品</h3>
            </header>
            <SpuDisplay data={itemForVerification.spu} />
          </div>
        </div>

        <div className={commonStyles.mid_of_3}>
          <OffersDisplay
            spu={itemForVerification.spu}
            selectedOfferGroups={labelState.offerGroupsWithError}
            toggleOfferGroup={toggleOfferGroup}
          />
        </div>

        <div className={commonStyles.right_of_3}>
          <SimilarSpus
            data={itemForVerification}
            selectedSpuIds={labelState.spuIdsToCombine}
            onChangeSelectedSpuIds={onChangeSelectedSpuIds}
          />
        </div>
      </>
    );
  })();

  return (
    <LabelLayout>
      <LabelLayoutHeader {...filterProps} type={LabelType.Verify} >
        <LoadableAntdButton
          onClick={() => dispatch(labelState)}
        >
          提交
        </LoadableAntdButton>
      </LabelLayoutHeader>
      {contentNode}
    </LabelLayout>
  );
};

interface VerificationState {
  queue: ItemForVerification[];
  index: number;
}

export const VerifyLabeler: FC<CommonLabelProps> = props => {
  const accountInfo = useAccountInfo();

  const { gender, c1, c2 } = props;

  const [{ labeler, brand, start, end }] = useUrlQueryState<LabelerURLQuery>();

  const [, setAccountLabelInfo] = useAccountLabelInfo();

  const [{
    data: { queue, index },
    loading,
    ready,
    error,
  }, dispatch] = useFlow<VerificationState, VerificationSubmission | void>(
    {
      queue: [],
      index: 0,
    },
    function* ({ call, put, get }, action) {
      if (action) {
        // 提交
        const {
          offerGroupsWithError,
          spuIdsToCombine
        } = action;

        const { index: currentIndex, queue: currentQueue }: GetNext<VerificationState> = yield get();

        const { labelId, spu: { spuId, images } } = currentQueue[currentIndex];

        // dont wait for this process
        apiService.postVerifyData({
          labelId,
          spuId,
          verifiedBy: accountInfo.username,
          spuImgUrl: images[0], // must be a primary image
          errorOffers: offerGroupsWithError.map(({ offers, offerUrl }) => ({
            errorImgUrl: offers[0].imgUrl,
            errorOfferUrl: offerUrl
          })),
        }).catch((e) => {
          message.error(e?.msg || e?.message || '提交失败，请重试');
        });

        let nextQueue = currentQueue;
        if (spuIdsToCombine.length) {
          try {
            yield call(async () => {
              let spuId0 = spuId;
              // async reduce
              for (const spuId1 of spuIdsToCombine) {
                const {
                  data: { toKeep },
                } = await apiService.postCombineProduct({
                  source: 'verify-select',
                  spuId0,
                  spuId1,
                });
                spuId0 = toKeep;
              }
            });

            // 移除后续的已经被 combine 掉的 spu
            nextQueue = currentQueue.filter((item, index) => index <= currentIndex || !spuIdsToCombine.includes(item.spu.spuId));
          } catch (e) {
            message.error(e?.msg || '提交失败，请重试');
            return;
          }
        }

        // 后面还有的话就向后一个
        if (nextQueue[currentIndex + 1]) {
          yield put({
            queue: nextQueue,
            index: currentIndex + 1,
          });
          return;
        }
      }

      // 拉取新数据
      const { data }: CallNext<GetVerifyData> = yield call(apiService.getVerifyData, {
        gender,
        category1: c1,
        category2: c2,
        labeler,
        brand,
        start_time: start && moment(start).format('YYYY-MM-DD HH:mm:ss'),
        end_time: end && moment(end).format('YYYY-MM-DD HH:mm:ss'),
      });

      setAccountLabelInfo(data.verifierInfo);

      yield put({
        queue: data.verifyData,
        index: 0,
      });
    }
  );

  useEffect(() => {
    dispatch();
  }, []);

  if (loading) {
    return <PureAntdLoading size="large" tip="正在获取标注数据..." />;
  }

  if (error) {
    console.log('error', error);
    const errorDescription = error?.msg || error?.data || '未知错误';
    return <Alert message="获取标注数据错误" showIcon description={errorDescription} type="error" />;
  }

  if (!ready) {
    // 只会在初始时刻出现的状态
    return null;
  }

  const currentItem = queue[index];

  if (!currentItem) {
    return (
      <LabelLayout>
        <LabelLayoutHeader {...props} type={LabelType.Verify} />
        <NoMoreLabeling />
      </LabelLayout>
    );
  }

  return (
    <VerifyLabelCore
      // key={currentItem.labelId}
      {...props}
      itemForVerification={currentItem}
      dispatch={dispatch}
    />
  );
};
